import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { BrandAndCategoryIdType, BrandType, CoKitchenPolygons } from 'utils/types';
import { CoKitchenType } from 'utils/types';
import { DashboardContext } from './context';
import { ENUM_CATEGORY, ENUM_SEARCH_TYPE } from 'utils/enum';
import { ROUTE_PREFIX, STORAGE_KEYS } from 'utils/constants';
import { LEKKI_COKITCHEN } from 'components/sidebar/constants';
import { apiService } from 'services/base.service';
import { ENUM_ROLE } from '@cokitchen/cokitchen-auth';
import { RolePermissionType } from 'utils/permissions-type';
import { permissionControls } from 'services/permissions';
import { useLocation } from 'react-router-dom';

const DashboardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [activeCokitchen, setActiveCokitchen] = useState({} as CoKitchenType);
  const [activeBrand, setActiveBrand] = useState({} as BrandType);
  const [category, setCategory] = useState<ENUM_CATEGORY>(ENUM_CATEGORY.FOOD);
  const [cokitchens, setCokitchens] = useState<CoKitchenType[]>([]);
  const [hasNotifications] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchFilter, setSearchFilter] = useState('' as ENUM_SEARCH_TYPE);
  const [rightSection, setRightSection] = useState<ReactNode>();
  const [isRightSectionWide, setIsRightSectionWide] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [mealInfo, setMealInfo] = useState<BrandAndCategoryIdType[]>([]);
  const [activePolygon, setActivePolygon] = useState<CoKitchenPolygons | null>(null);
  const [cokitchenPolygons, setCokitchenPolygons] = useState<CoKitchenPolygons[]>([]);
  const [cokitchenLoading, setCokitchenLoading] = useState(false);
  const [customSearch, setCustomSearch] = useState('');
  const [isCustomSearch, setIsCustomSearch] = useState(false);
  const [isFcb, setIsFcb] = useState(false);
  const [routePrefix, setRoutePrefix] = useState(ROUTE_PREFIX.FOOD);
  const [loggedInRole, setLoggedInRole] = useState('');
  const [permissions, setPermissions] = useState<RolePermissionType>(
    permissionControls[loggedInRole as keyof typeof permissionControls] ||
      ({} as RolePermissionType)
  );

  const { pathname } = useLocation();

  const trash = useMemo(() => {
    if (pathname.includes('search')) return false;

    return pathname.includes('trash');
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cat = localStorage.getItem(STORAGE_KEYS.CATEGORY) as ENUM_CATEGORY;
    setRoutePrefix(cat === ENUM_CATEGORY.FOOD ? ROUTE_PREFIX.FOOD : ROUTE_PREFIX.SHOP);
    setCategory(cat || ENUM_CATEGORY.FOOD);
  }, []);

  useEffect(() => {
    const storeRole = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_ROLE;
    if (storeRole) setLoggedInRole(storeRole);
    setPermissions(
      permissionControls[storeRole as keyof typeof permissionControls] || ({} as RolePermissionType)
    );
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.CATEGORY, category || ENUM_CATEGORY.FOOD);
  }, [category]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const activeCokitchen = localStorage.getItem(STORAGE_KEYS.ACTIVE_COKITCHEN);
    const defaultCokitchen = cokitchens.find((c) => c.name === LEKKI_COKITCHEN) ?? cokitchens[0];
    const active = activeCokitchen ? JSON.parse(activeCokitchen) : defaultCokitchen;
    setActiveCokitchen(active);
  }, [cokitchens]);

  useEffect(() => {
    if (!activeCokitchen?.id) return;
    localStorage.setItem(STORAGE_KEYS.ACTIVE_COKITCHEN, JSON.stringify(activeCokitchen));
  }, [activeCokitchen]);

  const refetchCokitchens = async () => {
    const res = await apiService.get('cokitchens', {
      isInternal: true
    });
    const data = res.data.data;
    localStorage.setItem(STORAGE_KEYS.COKITCHEN_LIST, JSON.stringify(data));
    setCokitchens(data);
  };

  return (
    <DashboardContext.Provider
      value={{
        activeCokitchen,
        setActiveCokitchen,
        category,
        setCategory,
        activeBrand,
        setActiveBrand,
        cokitchens,
        setCokitchens,
        refetchCokitchens,
        hasNotifications,
        searchValue,
        setSearchValue,
        rightSection,
        setRightSection,
        isRightSectionWide,
        setIsRightSectionWide,
        searchFilter,
        setSearchFilter,
        showMobileSidebar,
        setShowMobileSidebar,
        mealInfo,
        setMealInfo,
        activePolygon,
        setActivePolygon,
        cokitchenPolygons,
        setCokitchenPolygons,
        setCokitchenLoading,
        cokitchenLoading,
        trash,
        customSearch,
        setCustomSearch,
        isCustomSearch,
        setIsCustomSearch,
        isFcb,
        setIsFcb,
        routePrefix,
        loggedInRole,
        permissions
      }}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
